import { useLayoutEffect, useRef, useState } from 'react';
import { Link, Outlet, useLocation, useSearchParams } from 'react-router-dom';

import { useEmbedBootstrap } from '../use-widget-bootstrap';
import { BrandExpectNotice } from './brand-expect-notice';
import { EmbedSiteFooter } from '../widget-ui/embed-site-footer';
import { MediaAvatar } from '../widget-ui/media-avatar';
import { useQuerySuffix } from '../widget-hooks/useQuerySuffix';
import { PATH } from '../widget-lib/path-routes';



const scrollCss =
  '[-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden';

function ScrollableNav({ children }: { children: React.ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [leftVisible, setLeftVisible] = useState(false);
  const [rightVisible, setRightVisible] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const check = () => {
      const canScroll = el!.scrollWidth > el!.clientWidth + 0.5;
      setLeftVisible(canScroll && el!.scrollLeft > 0);
      setRightVisible(canScroll && el!.scrollLeft + el!.clientWidth + 0.5 < el!.scrollWidth);
    };
    check();
    el.addEventListener('scroll', check, { passive: true });
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', check);
      ro.disconnect();
    };
  }, [children]);

  const scrollBy = (dir: 'left' | 'right') => {
    const el = ref.current;
    if (!el) return;
    el.scrollBy({ left: dir === 'left' ? -el.clientWidth * 0.7 : el.clientWidth * 0.7, behavior: 'smooth' });
  };

  return (
    <div className="flex w-full items-center">
      {leftVisible && (
        <button
          type="button"
          className="shrink-0 w-5 flex items-center justify-center self-stretch bg-gradient-to-r from-[var(--bg-elevated)] to-transparent text-sm text-[var(--text-muted)] hover:text-[var(--text)] focus-visible:outline-none"
          onClick={() => scrollBy('left')}
          aria-label="Назад"
        >
          ‹
        </button>
      )}
      <div ref={ref} className={`flex flex-1 min-w-0 flex-nowrap items-center gap-1.5 overflow-x-auto px-2 py-0.5 ${scrollCss}`}>
        {children}
      </div>
      {rightVisible && (
        <button
          type="button"
          className="shrink-0 w-5 flex items-center justify-center self-stretch bg-gradient-to-l from-[var(--bg-elevated)] to-transparent text-sm text-[var(--text-muted)] hover:text-[var(--text)] focus-visible:outline-none"
          onClick={() => scrollBy('right')}
          aria-label="Вперёд"
        >
          ›
        </button>
      )}
    </div>
  );
}

export function BookingShell() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { me, accessToken, effectiveModules, unpaidBookingsCount } = useEmbedBootstrap();
  const pathname = location.pathname.replace(/\/+$/, '') || '/';
  const suffix = useQuerySuffix();

  const modulesForNav = effectiveModules ?? me?.modules ?? null;

  const navLinks = [
    { label: 'Расписание', path: PATH.schedule },
    { label: 'QR-коды', path: PATH.myAccess, module: 'qrcode', requiresAuth: true },
    { label: 'Услуги', path: PATH.services },
    { label: 'Тренеры', path: PATH.trainers, module: 'fitness' },
    { label: 'Мои записи', path: PATH.bookings, requiresAuth: true },
    { label: 'Абонементы', path: PATH.subscriptionPlans, module: 'subscriptions' },
    { label: 'Об организации', path: PATH.about }
  ].filter((link) => {
    if (link.requiresAuth && !accessToken) return false;
    if (link.module) {
      return modulesForNav?.[link.module] === true;
    }
    return true;
  });

  const isNavPath = navLinks.some((l) => pathname === l.path || pathname.startsWith(`${l.path}/`));

  // Ссылка «← Каталог»: чистим все org/schedule-специфичные params — возвращает на витрину чисто
  const catalogHref = (() => {
    const embedGlobal = searchParams.get('embed_global') === '1';
    if (!embedGlobal) return null;
    const p = new URLSearchParams();
    p.set('embed_global', '1');
    const parentOriginVal = searchParams.get('embed_parent_origin');
    if (parentOriginVal) p.set('embed_parent_origin', parentOriginVal);
    const wnVal = searchParams.get('wn');
    if (wnVal) p.set('wn', wnVal);
    const widgetChannelVal = searchParams.get('widget_channel');
    if (widgetChannelVal) p.set('widget_channel', widgetChannelVal);
    const qs = p.toString();
    return `/embed${PATH.home}${qs ? `?${qs}` : ''}`;
  })();

  const userBadge = accessToken && me ? (
    <Link
      to={`${PATH.account}${suffix}`}
      className="ml-auto flex shrink-0 items-center gap-1.5 pl-2 text-xs text-[var(--text-muted)] hover:text-[var(--text)]"
    >
      <MediaAvatar photoUrl={me.avatar_url} label={me.full_name || me.email} sizeClass="h-6 w-6" roundedClass="rounded-full" />
      <span className="max-w-[80px] truncate">{me.full_name || me.email}</span>
    </Link>
  ) : null;

  const authLinks = !accessToken ? (
    <Link
      to={`${PATH.signIn}${suffix}`}
      className="shrink-0 rounded-full px-4 py-2.5 text-xs font-semibold leading-tight tracking-tight transition-all duration-200 bg-[var(--accent-bg)] text-[var(--accent-fg)] shadow-sm hover:bg-[var(--accent-bg-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]"
    >
      Вход
    </Link>
  ) : null;

  const navContent = isNavPath ? (
    <>
      {catalogHref ? (
        <button
          type="button"
          onClick={() => {
            window.location.href = catalogHref;
          }}
          className="shrink-0 rounded-full px-4 py-2.5 text-xs font-semibold leading-tight tracking-tight transition-all duration-200 bg-[var(--accent-bg)] text-[var(--accent-fg)] shadow-sm hover:bg-[var(--accent-bg-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]"
        >
          ← Каталог
        </button>
      ) : null}
      {navLinks.map((link) => {
        const active = pathname === link.path || pathname.startsWith(`${link.path}/`);
        const isBookings = link.path === PATH.bookings;
        const showBadge = isBookings && unpaidBookingsCount > 0;
        return (
          <Link
            key={link.path}
            to={`${link.path}${suffix}`}
            className={`shrink-0 rounded-full px-4 py-2.5 text-xs font-semibold leading-tight tracking-tight transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)] flex items-center gap-1.5 ${
              active
                ? 'bg-[var(--accent-bg)] text-[var(--accent-fg)] shadow-sm'
                : 'bg-[var(--bg-soft)] text-[var(--text-muted)] hover:bg-[var(--border)] hover:text-[var(--text)]'
            }`}
          >
            <span>{link.label}</span>
            {showBadge && (
              <span className="inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[9px] font-bold text-white shadow-sm shrink-0">
                {unpaidBookingsCount}
              </span>
            )}
          </Link>
        );
      })}
      {authLinks}
      {userBadge}
    </>
  ) : (
    <>
      {catalogHref ? (
        <button
          type="button"
          onClick={() => {
            window.location.href = catalogHref;
          }}
          className="shrink-0 rounded-full px-4 py-2.5 text-xs font-semibold leading-tight tracking-tight transition-all duration-200 bg-[var(--accent-bg)] text-[var(--accent-fg)] shadow-sm hover:bg-[var(--accent-bg-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]"
        >
          ← Каталог
        </button>
      ) : (
        <Link
          to={`${PATH.schedule}${suffix}`}
          className="shrink-0 rounded-full px-4 py-2.5 text-xs font-semibold leading-tight tracking-tight transition-all duration-200 bg-[var(--bg-soft)] text-[var(--text-muted)] hover:bg-[var(--border)] hover:text-[var(--text)]"
        >
          ← Назад
        </Link>
      )}
      {authLinks}
      {userBadge}
    </>
  );

  return (
    <div
      id="domcrm-embed-booking"
      className="flex min-h-screen flex-col bg-[var(--bg)]"
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
    >
      <BrandExpectNotice />
      {navContent ? (
        <div
          className="border-b border-[var(--border)] bg-[var(--bg-elevated)] py-1"
          style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
        >
          <ScrollableNav>{navContent}</ScrollableNav>
        </div>
      ) : null}
      <main
        id="domcrm-embed-booking-main"
        className="flex w-full min-w-0 flex-1 flex-col px-3 sm:px-5 pt-3 sm:pt-4"
        aria-label="Запись на занятие"
      >
        <Outlet />
      </main>
      <EmbedSiteFooter />
    </div>
  );
}
